import ReactCrop from 'react-image-crop';
import { GifCodec } from 'gifwrap';
import blobToBuffer from 'blob-to-buffer';
import { AVATAR_SIZE } from '../constants';
import * as Pica from 'pica';

function canvasHasTransparency(
  context: CanvasRenderingContext2D,
  width: number,
  height: number
) {
  const imageData = context.getImageData(0, 0, width, height);
  for (let i = 0; i < imageData.data.length; i += 4) {
    if (imageData.data[i + 3] < 255) {
      return true;
    }
  }
  return false;
}

function blobToBufferPromise(blob: Blob) {
  return new Promise<Buffer>((resolve, reject) => {
    blobToBuffer(blob, (err, buffer) => {
      if (err) {
        reject(err);
      }
      resolve(buffer);
    });
  });
}

async function decodeGif(file: File) {
  const buffer = await blobToBufferPromise(file);
  const codec = new GifCodec();
  const gif = await codec.decodeGif(buffer);
  return gif;
}

export async function isAnimatedGif(file: File) {
  if (file.type !== 'image/gif') {
    return false;
  }

  const gif = await decodeGif(file);
  return gif.frames.length > 1;
}

const getCanvasContext2d = (
  canvas: HTMLCanvasElement
): CanvasRenderingContext2D =>
  canvas.getContext('2d') as CanvasRenderingContext2D;

const createCanvas = (width = 100, height = width): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const cropCanvas = (
  canvas: HTMLCanvasElement,
  x: number,
  y: number,
  width: number,
  height: number
) => {
  const ctx = getCanvasContext2d(canvas);
  const imageData = ctx.getImageData(x, y, width, height);
  ctx.canvas.width = width;
  ctx.canvas.height = height;
  ctx.putImageData(imageData, 0, 0);
};

export async function cropImage(
  file: File,
  image: HTMLImageElement,
  pixelCrop: ReactCrop.Crop,
  imageQuality: number
) {
  if (await isAnimatedGif(file)) {
    if (image.width !== AVATAR_SIZE || image.height !== AVATAR_SIZE) {
      throw new Error('widgets.avatarEdit.error.cannotResizeAnimatedGifs');
    }
    return file;
  }

  // If no resizing is needed, return the original file
  if (
    pixelCrop.x === 0 &&
    pixelCrop.y === 0 &&
    pixelCrop.width === AVATAR_SIZE &&
    pixelCrop.height === AVATAR_SIZE &&
    image.width === AVATAR_SIZE &&
    image.height === AVATAR_SIZE
  ) {
    return file;
  }

  // Create a canvas element, draw cropped image onto it
  // and return it as a Blob
  const canvas = createCanvas(pixelCrop.width!, pixelCrop.height!);
  let outputType = file.type;
  let hasTransparency = false;
  if (image) {
    const ctx = getCanvasContext2d(canvas);
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0, image.width, image.height);
    cropCanvas(
      canvas,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width!,
      pixelCrop.height!
    );
    hasTransparency = canvasHasTransparency(ctx, canvas.width, canvas.height);

    // convert non-transparent PNGs to jpg to save on file size
    if (file.type === 'image/png' && !hasTransparency) {
      outputType = 'image/jpeg';
    }
  }

  const pica = new Pica();
  const blob = await pica
    .resize(canvas, createCanvas(AVATAR_SIZE, AVATAR_SIZE), {
      unsharpAmount: 80,
      unsharpRadius: 0.6,
      unsharpThreshold: 2,
      alpha: hasTransparency,
    })
    .then(result => pica.toBlob(result, outputType, imageQuality));

  return blob;
}
